<template>
  <v-row dense>
    <v-col>
      <v-form>
        <v-card>
          <v-card-text>
            <h1 class="headline font-weight-medium mb-3">
              {{ isNew ? "Створення користувача" : "Редагування користувача" }}
            </h1>
            <v-row>
              <v-col class="pb-0 mb-n3">
                <CustomTextField
                  v-model="name"
                  outlined
                  label="Логін"
                  :error-messages="$v.name.$error ? errors.NAME : []"
                  :hide-details="false"
                  @input="$v.name.$touch()"
                />
              </v-col>
              <v-col class="pb-0 mb-n3">
                <UserRolesSelect
                  v-model="role"
                  :disabled="!canChangeRole"
                  dense
                  clearable
                  filled
                  :error-messages="$v.role.$error ? errors.ROLE : []"
                  :hide-details="false"
                  @input="$v.role.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="pb-0 mb-n3">
                <CustomTextField
                  v-model="title"
                  outlined
                  label="Подання"
                  :error-messages="$v.title.$error ? errors.TITLE : []"
                  :hide-details="false"
                  @input="$v.title.$touch()"
                />
              </v-col>
              <v-col class="pb-0 mb-n4">
                <CustomTextField
                  v-model="email"
                  outlined
                  label="Email"
                  :error-messages="$v.email.$error ? errors.EMAIL : []"
                  :hide-details="false"
                  @input="$v.email.$touch()"
                />
              </v-col>
              <v-col class="pb-0 mb-n4">
                <CustomTextField
                  v-model="comment"
                  outlined
                  label="Коментарій"
                  :hide-details="false"
                  @input="$v.comment.$touch()"
                />
              </v-col>
            </v-row>
            <v-row v-if="isNew">
              <v-col
                cols="6"
                class="pb-0 mb-n4"
              >
                <PasswordField
                  v-model="newPassword"
                  outlined
                  :error-messages="$v.newPassword.$error ? errors.NEW_PASSWORD : []"
                  :hide-details="false"
                  @input="$v.newPassword.$touch()"
                />
              </v-col>
              <v-col
                v-if="isClientRoleSelected"
                cols="6"
                class="pb-0 mb-n4"
              >
                <ClientSelect
                  v-model="client"
                  label="Клієнт"
                  :clearable="true"
                  :error-messages="$v.client.$error ? errors.CLIENT : []"
                  :hide-details="false"
                  @input="$v.client.$touch()"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <PrimaryBtn
              :has-warnings="$v.$invalid"
              :loading="loading"
              @click="save"
            />
            <v-btn @click="$router.go(-1)">
              Відмінити
            </v-btn>
            <v-spacer />
            <ChangePassword
              v-if="!isNew && isAdmin"
              :user-id="id"
            />
          </v-card-actions>
        </v-card>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import UserRolesSelect from "@/components/enums/UserRoleSelect.vue";
import ChangePassword from "./ChangePassword.vue";
import * as usersApi from "@/backend/usersApi.js";
import ClientSelect from "@/components/selects/ClientSelect.vue";
import auth from "@/auth.js";
import { ERRORS } from "@/helpers";
import { required, minLength, requiredIf } from "vuelidate/lib/validators";
import PrimaryBtn from "@/components/PrimaryBtn.vue";

export default {
  components: {
    ChangePassword,
    UserRolesSelect,
    ClientSelect,
    PrimaryBtn,
  },
  props: ["id"],
  data: () => ({
    title: "",
    name: "",
    email: "",
    comment: "",
    role: 0,
    newPassword: "",
    showNewPassword: false,
    roles: [],
    loading: false,
    isChanged: false,
    client: null,
  }),

  validations() {
    return {
      name: {
        required,
      },
      title: {
        required,
      },
      role: {
        required,
      },
      newPassword: {
        requiredIf: requiredIf(() => {
          if (this.isNew) {
            return {
              required,
            };
          }
        }),
        minLength: minLength(8),
      },
      email: {
        required,
      },
      comment: {},
      client: {
        requiredIf: requiredIf(() => {
          if (this.role === 4) {
            return {
              id: {
                required,
              },
              title: {
                required,
              },

            };
          }
        }),
      },
    };
  },

  computed: {
    isNew() {
      return +this.id ? false : true;
    },
    canChangeRole() {
      return this.isAdmin && auth.getUser().name !== this.name;
    },
    isClientRoleSelected() {
      return auth.isClient(this.role);
    },
    isAdmin() {
      return auth.isAdmin();
    },
    errors() {
      return {
        TITLE: [ERRORS.REQUIRED],
        NAME: [ERRORS.REQUIRED],
        ROLE: [ERRORS.REQUIRED],
        EMAIL: [ERRORS.REQUIRED],
        CLIENT: [ERRORS.REQUIRED],
        NEW_PASSWORD: [ERRORS.PASSWORD],
      };
    },
  },
  async created() {
    if (+this.id) {
      const user = await usersApi.getUser(this.id);
      this.title = user.title;
      this.name = user.name;
      this.email = user.email;
      this.comment = user.comment;
      this.role = user.role;
      this.client = user.clientId ? { id: user.clientId } : null;
    }
  },
  methods: {
    async save() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.loading = true;

      try {
        if (!this.isNew) {
          await usersApi
            .updateUser(this.id, {
              name: this.name,
              title: this.title,
              email: this.email,
              comment: this.comment,
              role: this.role,
              ...(this.isClientRoleSelected && { clientId: this.client?.id }),
            });
        } else {
          await usersApi
            .createUser({
              name: this.name,
              title: this.title,
              password: this.newPassword,
              email: this.email,
              comment: this.comment,
              role: this.role,
              ...(this.isClientRoleSelected && { clientId: this.client?.id }),
            });
        }

        this.$notifySuccess();
        this.$router.go(-1);
      } catch {
        this.$notifyWarning();
      } finally {
        this.loading = false;
        this.isChanged = false;
      }
    },
  },
};
</script>